/// <reference types="vite/client" />
/// <reference types="w3c-web-hid" />

import { computed, onBeforeUnmount, ref, shallowRef } from 'vue'

import { useLogger } from './useLogger.js'

// ! "Global" state
const scaleDevice = shallowRef<null | HIDDevice>(null)
const isScaleOpened = ref(false)
export interface ScaleWeightData {
  units: 'kg' | 'lb'
  value: number
  state: 'settled' | 'changing'
}
const isLocalEnvironment = import.meta.env.VITE_ENVIRONMENT === 'local'
const logger = useLogger('client-frontend', 'scale')
export type OnWeightUpdateSignature = (weightData: ScaleWeightData) => void
export type OnDisconnectSignature = (event: HIDConnectionEvent) => void
export type OnConnectSignature = (event: HIDConnectionEvent) => void

export const useScale = (
  onWeightUpdate: OnWeightUpdateSignature,
  onConnect: OnConnectSignature = (event) => {
    if (isLocalEnvironment) {
      logger.debug('Connected', { device: event.device })
    }
  },
  onDisconnect: OnDisconnectSignature = (event) => {
    if (isLocalEnvironment) {
      logger.debug('Disconnected', { device: event.device })
    }
  },
  debug = false,
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
) => {
  const removeScaleDevice = (): void => {
    scaleDevice.value = null
  }
  const connectHandler = (event: HIDConnectionEvent): void => {
    onConnect(event)
  }
  const disconnectHandler = (event: HIDConnectionEvent): void => {
    onDisconnect(event)
    removeScaleDevice()
  }

  if (navigator.hid) {
    navigator.hid.addEventListener('connect', connectHandler)
    navigator.hid.addEventListener('disconnect', disconnectHandler)
  }

  onBeforeUnmount(() => {
    if (navigator.hid) {
      navigator.hid.removeEventListener('connect', connectHandler)
      navigator.hid.removeEventListener('disconnect', disconnectHandler)
    }
  })
  const isScalePaired = computed(() => {
    return scaleDevice.value !== null
  })
  const updateScale = async (): Promise<void> => {
    if (navigator.hid) {
      try {
        const devices = await navigator.hid.getDevices()
        if (devices.length) {
          const device = devices.find((device) => device.vendorId === 3768)
          if (device) {
            scaleDevice.value = device
          } else {
            removeScaleDevice()
          }
        } else {
          removeScaleDevice()
        }
      } catch (err) {
        logger.error('Operation failed', { error: String(err) })
      }
    } else {
      removeScaleDevice()
    }
  }
  const requestScale = async (): Promise<boolean> => {
    if (navigator.hid) {
      const devices = await navigator.hid.requestDevice({
        filters: [
          { vendorId: 3768 }, // Mettler Toledo
        ],
      })
      const device = devices?.[0]
      if (device) {
        scaleDevice.value = device
        return true
      }
    }
    return false
  }
  const connectScale = async (): Promise<boolean> => {
    if (navigator.hid) {
      try {
        const deviceList = await navigator.hid.getDevices()
        const device = deviceList?.[0]
        if (device) {
          scaleDevice.value = device
          return true
        }
        return false
      } catch (err) {
        logger.error('Operation failed', { error: String(err) })
      }
    }
    return false
  }

  const openScale = async (): Promise<void> => {
    if (scaleDevice.value) {
      try {
        await scaleDevice.value.open()
        isScaleOpened.value = true
      } catch (err) {
        isScaleOpened.value = false
        if (debug) {
          logger.error('Encountered error opening Scale Device', { error: String(err) })
        }
      }
    }
  }
  const closeScale = async (): Promise<void> => {
    if (scaleDevice.value) {
      try {
        scaleDevice.value.oninputreport = null
        await scaleDevice.value.close()
        isScaleOpened.value = false
      } catch (err) {
        logger.error('Encountered error closing Scale Device', { error: String(err) })
      }
    }
  }
  const initiateRead = async (): Promise<void> => {
    if (scaleDevice.value) {
      scaleDevice.value.oninputreport = (ev): void => {
        const data = new Uint8Array(ev.data.buffer)
        if (data.length >= 5) {
          const [stable, unit, multiplier, valueRemainder, valueBase] = data
          if (
            typeof valueBase === 'number' &&
            typeof multiplier === 'number' &&
            typeof valueRemainder === 'number'
          ) {
            const weight = (valueBase * multiplier + valueRemainder) / 100
            onWeightUpdate({
              units: unit === 12 ? 'lb' : 'kg',
              value: weight,
              state: stable === 4 ? 'settled' : 'changing',
            })
          }
        }
      }
    }
  }
  const stopRead = async (): Promise<void> => {
    if (scaleDevice.value) {
      try {
        scaleDevice.value.oninputreport = null
        removeScaleDevice()
      } catch (err) {
        logger.error('Encountered error closing Scale Device', { error: String(err) })
      }
    }
  }
  return {
    updateScale,
    openScale,
    closeScale,
    removeScaleDevice,
    initiateRead,
    stopRead,
    requestScale,
    connectScale,
    scaleDevice,
    isScaleOpened,
    isScalePaired,
  }
}
